import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Folder } from 'app/interfaces/folder.interface';
import { QueryFilter } from 'app/interfaces/query-filter.interface';
import { Query } from 'app/interfaces/query.interface';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FolderService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}`;

  private _folders$ = new BehaviorSubject<Folder[]>([]);
  readonly folders$ = this._folders$.asObservable();

  refreshFolders(): void {
    this.getFolders()
      .pipe(tap((folders) => this._folders$.next(folders)))
      .subscribe();
  }

  getFolders(): Observable<Folder[]> {
    return this.http.get<Folder[]>(`${this.baseUrl}/folders`);
  }

  createFolder(name: string): Observable<Folder> {
    return this.http.post<Folder>(`${this.baseUrl}/folders`, { name });
  }

  renameFolder(id: string, name: string): Observable<Folder> {
    return this.http.put<Folder>(`${this.baseUrl}/folders/${id}`, { name });
  }

  deleteFolder(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/folders/${id}`);
  }

  getFolderQueries(folderId: string, filter?: QueryFilter): Observable<Query[]> {
    let params = new HttpParams();
    if (filter) {
      if (filter.name) {
        params = params.set('name', filter.name);
      }
      if (filter.database_id) {
        params = params.set('database_id', filter.database_id);
      }
      if (filter.creator_email) {
        params = params.set('creator_email', filter.creator_email);
      }
      if (filter.tags?.length) {
        filter.tags.forEach((t) => (params = params.append('tags[]', t)));
      }
      if (filter.table_name) {
        params = params.set('table_name', filter.table_name);
      }
      if (filter.sort_by) {
        params = params.set('sort_by', filter.sort_by);
      }
      if (!filter.show_active && filter.show_deleted) {
        params = params.set('only_deleted', '1');
      } else if (filter.show_active && filter.show_deleted) {
        params = params.set('with_deleted', '1');
      }
    }
    return this.http.get<Query[]>(`${this.baseUrl}/folders/${folderId}/queries`, { params });
  }

  getQueryFolders(queryId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/queries/${queryId}/folders`);
  }

  attachQuery(folderId: string, queryId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/folders/${folderId}/queries/${queryId}`, {});
  }

  detachQuery(folderId: string, queryId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/folders/${folderId}/queries/${queryId}`);
  }
}
